// FAR-78: the Modifications panel's model. Pure, so its rules -- titles,
// scope, reach, which edits a rule leaves standing, the counts -- are tested
// without a browser.
//
// A plan's modifications are of two kinds:
//   Rules       -- one card per DOSTarget / Qty / PercentOfMinInv rule.
//   Cell edits  -- the Units a planner typed (the "Cell" change type), one per
//                  SKU-month, grouped by SKU.

export const CELL = 'Cell'
export const DOS_TARGET = 'DOSTarget'

export const isCell = (m) => m.changeType === CELL

// Newest first: a new modification is position 1 (top). The engine applies
// them oldest first, so the newest wins (FAR-78 Open Item 2).
export function newestFirst(mods) {
  return [...(mods || [])].sort((a, b) => (a.listPosition || 0) - (b.listPosition || 0))
}

export function splitModifications(mods) {
  const all = newestFirst(mods)
  return { rules: all.filter((m) => !isCell(m)), cells: all.filter(isCell) }
}

// §3 "100% of Min DOS · 9 months". Older rules keep their saved name.
export function ruleTitle(rule, monthCount) {
  if (rule.changeType === DOS_TARGET) {
    const pcts = [...new Set(Object.values(rule.monthValues || {}))]
    const pct = pcts.length === 1 ? `${pcts[0]}%` : 'Varying %'
    const n = monthCount ?? Object.keys(rule.monthValues || {}).length
    return `${pct} of ${rule.basis} DOS · ${n} month${n === 1 ? '' : 's'}`
  }
  return rule.name || 'Modification'
}

// The SKUs a rule applies to, among the board's rows.
export function ruleReach(rule, rows) {
  const list = rows || []
  switch (rule.scope) {
    case 'AllProduct': return list.map((r) => r.itemNumber)
    case 'BriteCode': return list.filter((r) => String(r.briteCode) === String(rule.identifier)).map((r) => r.itemNumber)
    case 'ItemNumber': return list.some((r) => String(r.itemNumber) === String(rule.identifier)) ? [String(rule.identifier)] : []
    case 'Items': return (rule.items || []).map(String)
    default: return []
  }
}

// §3 scope line: "3 SKUs: Black Beans, Chili, Refried", or "All SKUs (480)".
export function ruleScopeLabel(rule, rows, max = 3) {
  if (rule.scope === 'AllProduct') return `All SKUs (${(rows || []).length})`
  const byItem = Object.fromEntries((rows || []).map((r) => [String(r.itemNumber), r]))
  const reach = ruleReach(rule, rows)
  const names = reach.map((id) => byItem[id]?.description || id)
  const shown = names.slice(0, max).join(', ')
  const more = names.length > max ? ` +${names.length - max} more` : ''
  const prefix = rule.scope === 'BriteCode' ? `Brite ${rule.identifier} · ` : ''
  return `${prefix}${reach.length} SKU${reach.length === 1 ? '' : 's'}${reach.length ? `: ${shown}${more}` : ''}`
}

// §4 "kept n edits": typed Units inside a rule's reach and months stay as typed
// (typed Units are above every rule), so the rule left them alone.
export function keptEdits(rule, cells, rows) {
  if (rule.isIgnored) return 0
  const reach = new Set(ruleReach(rule, rows))
  const months = rule.changeType === DOS_TARGET || !rule.defaultValue
    ? new Set(Object.keys(rule.monthValues || {}))
    : null // a rule with a default applies to every month
  let n = 0
  for (const c of cells || []) {
    if (c.isIgnored || !reach.has(String(c.identifier))) continue
    for (const iso of Object.keys(c.monthValues || {})) if (!months || months.has(iso)) n++
  }
  return n
}

// §6 Cell edits section: one group per SKU, each a list of {iso, units, base}.
// baseRowsByItem/monthKeyOf give the Base Plan value for "Base -> typed".
export function cellGroups(cells, rows, baseRowsByItem, monthKeyOf) {
  const byItem = Object.fromEntries((rows || []).map((r) => [String(r.itemNumber), r]))
  return (cells || []).filter((c) => !c.isIgnored).map((c) => {
    const id = String(c.identifier)
    const edits = Object.entries(c.monthValues || {}).sort(([a], [b]) => a.localeCompare(b)).map(([iso, units]) => {
      const key = monthKeyOf?.(iso)
      const base = key ? baseRowsByItem?.[id]?.[`${key}_cases`] : undefined
      // Round before subtracting, so the difference matches the numbers shown
      // ("Base 4,800 -> 4,800" is +0, never -0 or +0.4).
      const b = base == null ? null : Math.round(base)
      const u = Math.round(units)
      return { iso, units, base: base ?? null, delta: b == null ? null : u - b }
    })
    return { mod: c, itemNumber: id, description: byItem[id]?.description || '', edits }
  }).filter((g) => g.edits.length > 0)
}

// §2 header chips.
export function panelCounts(mods) {
  const { rules, cells } = splitModifications(mods)
  return {
    rules: rules.filter((r) => !r.isIgnored).length,
    ignored: rules.filter((r) => r.isIgnored).length,
    edits: cells.filter((c) => !c.isIgnored).reduce((n, c) => n + Object.keys(c.monthValues || {}).length, 0),
  }
}

// One month of a Cell edit removed: the monthValues left ({} = delete it).
export function withoutMonth(cell, iso) {
  const next = { ...(cell.monthValues || {}) }
  delete next[iso]
  return next
}

// "+200", "-1,500", "+0".
export function formatDelta(d) {
  if (d == null) return ''
  return `${d >= 0 ? '+' : '−'}${Math.abs(d).toLocaleString('en-US')}`
}
